/**
 * The Search view's filters — free metadata filtering (name, type, area,
 * size, dates), plus drilling into a By Type / By Arche group. Pure: no React.
 *
 * Everything here becomes query parameters on `/vault/documents` and its
 * `by-kind` / `by-arche` summaries, which share ONE server-side WHERE — so a
 * group's count is exactly what the list shows when narrowed to it. None of
 * it reaches the billed semantic search.
 */
import type { VaultDocumentMetadataFilters, VaultAreaDto } from '@/lib/api';
import { VAULT_FILE_KIND_LABELS, type VaultFileKind } from '@/lib/vault-file-kinds';
import { AREA_NAMES } from '../../lib/locations';
import { formatBytes } from '@/components/files/file-dialog/format';

export const ALL_AREAS: readonly VaultAreaDto[] = ['documents', 'arche-saves', 'my-fias'];

/** Age presets, as days back from now. */
export const AGE_PRESETS = [
  { value: '1d', label: '1 day', days: 1 },
  { value: '1w', label: '1 week', days: 7 },
  { value: '1m', label: '1 month', days: 30 },
  { value: '3m', label: '3 months', days: 90 },
  { value: '6m', label: '6 months', days: 180 },
  { value: '1y', label: '1 year', days: 365 },
] as const;
export type AgePreset = (typeof AGE_PRESETS)[number]['value'] | '';

/** Size presets, in bytes. */
export const SIZE_PRESETS = [
  { value: '100k', label: '100 KB', bytes: 100 * 1024 },
  { value: '1m', label: '1 MB', bytes: 1024 * 1024 },
  { value: '10m', label: '10 MB', bytes: 10 * 1024 * 1024 },
  { value: '100m', label: '100 MB', bytes: 100 * 1024 * 1024 },
  { value: '1g', label: '1 GB', bytes: 1024 * 1024 * 1024 },
] as const;
export type SizePreset = (typeof SIZE_PRESETS)[number]['value'] | '';

export interface SearchFilterState {
  /** Case-insensitive part of the name. */
  text: string;
  /** Empty = every kind. */
  kinds: VaultFileKind[];
  /** The areas to include. All three by default — nothing the user pays for
   *  is out of sight. */
  areas: VaultAreaDto[];
  largerThan: SizePreset;
  smallerThan: SizePreset;
  modifiedNewerThan: AgePreset;
  modifiedOlderThan: AgePreset;
  addedNewerThan: AgePreset;
  addedOlderThan: AgePreset;
}

export const EMPTY_SEARCH_FILTERS: SearchFilterState = {
  text: '',
  kinds: [],
  areas: [...ALL_AREAS],
  largerThan: '',
  smallerThan: '',
  modifiedNewerThan: '',
  modifiedOlderThan: '',
  addedNewerThan: '',
  addedOlderThan: '',
};

/** A group the person clicked into from By Type or By Arche. */
export type SearchDrill =
  | { by: 'kind'; kind: VaultFileKind; label: string }
  | {
      by: 'arche';
      group: 'arche' | 'uploads' | 'fias';
      archeId: string | null;
      label: string;
    };

const ageLabel = (v: AgePreset) => AGE_PRESETS.find((p) => p.value === v)?.label ?? '';
const sizeLabel = (v: SizePreset) => SIZE_PRESETS.find((p) => p.value === v)?.label ?? '';

export function moreFiltersActive(s: SearchFilterState): boolean {
  return !!(
    s.largerThan ||
    s.smallerThan ||
    s.modifiedNewerThan ||
    s.modifiedOlderThan ||
    s.addedNewerThan ||
    s.addedOlderThan
  );
}

/** The area set a no-arche group covers: its files are told apart by area. */
function drillAreas(drill: SearchDrill, areas: readonly VaultAreaDto[]): VaultAreaDto[] {
  if (drill.by !== 'arche' || drill.group === 'arche') return [...areas];
  const allowed: VaultAreaDto[] =
    drill.group === 'fias' ? ['arche-saves'] : ['documents', 'my-fias'];
  return areas.filter((a) => allowed.includes(a));
}

/**
 * The wire filters for a state (and an optional drill-in). Date bounds come
 * from a clock FLOORED TO THE HOUR: they land in the React Query key, and a
 * raw `Date.now()` would mint a new key every render. An hour of slack is
 * nothing to a filter whose finest preset is a day.
 */
export function searchFiltersToQuery(
  s: SearchFilterState,
  drill: SearchDrill | null,
  now: number = Date.now(),
): VaultDocumentMetadataFilters {
  const HOUR = 3_600_000;
  const floored = Math.floor(now / HOUR) * HOUR;
  const ago = (v: AgePreset) => {
    const preset = AGE_PRESETS.find((p) => p.value === v);
    return preset ? new Date(floored - preset.days * 24 * HOUR).toISOString() : undefined;
  };
  const bytes = (v: SizePreset) => SIZE_PRESETS.find((p) => p.value === v)?.bytes;
  const areas = drill ? drillAreas(drill, s.areas) : s.areas;
  const kinds = drill?.by === 'kind' ? [drill.kind] : s.kinds;
  const query: VaultDocumentMetadataFilters = {};
  if (s.text.trim()) query.search = s.text.trim();
  if (kinds.length > 0) query.kinds = kinds;
  // All three areas is no filter at all. None would match nothing — the view
  // shows that itself (`matchesNothing`) and never sends an empty set.
  if (areas.length > 0 && areas.length < ALL_AREAS.length) query.areas = areas;
  const larger = bytes(s.largerThan);
  const smaller = bytes(s.smallerThan);
  if (larger !== undefined) query.minSizeBytes = larger;
  if (smaller !== undefined) query.maxSizeBytes = smaller;
  const updatedAfter = ago(s.modifiedNewerThan);
  const updatedBefore = ago(s.modifiedOlderThan);
  const createdAfter = ago(s.addedNewerThan);
  const createdBefore = ago(s.addedOlderThan);
  if (updatedAfter) query.updatedAfter = updatedAfter;
  if (updatedBefore) query.updatedBefore = updatedBefore;
  if (createdAfter) query.createdAfter = createdAfter;
  if (createdBefore) query.createdBefore = createdBefore;
  if (drill?.by === 'arche') {
    query.sourceArche = drill.group === 'arche' && drill.archeId ? drill.archeId : 'none';
  }
  return query;
}

/** True when the areas chosen leave nothing to show (every area switched off,
 *  or a drill-in outside them) — the view says so instead of asking. */
export function matchesNothing(s: SearchFilterState, drill: SearchDrill | null): boolean {
  return (drill ? drillAreas(drill, s.areas) : s.areas).length === 0;
}

export interface FilterChip {
  key: string;
  label: string;
  clear: () => void;
}

/**
 * Every ACTIVE filter as a removable chip — so an applied filter is never
 * hidden inside a closed popover.
 */
export function describeSearchFilters(
  s: SearchFilterState,
  onChange: (next: SearchFilterState) => void,
): FilterChip[] {
  const chips: FilterChip[] = [];
  if (s.text.trim()) {
    chips.push({
      key: 'text',
      label: `Name contains “${s.text.trim()}”`,
      clear: () => onChange({ ...s, text: '' }),
    });
  }
  for (const kind of s.kinds) {
    chips.push({
      key: `kind:${kind}`,
      label: VAULT_FILE_KIND_LABELS[kind],
      clear: () => onChange({ ...s, kinds: s.kinds.filter((k) => k !== kind) }),
    });
  }
  if (s.areas.length < ALL_AREAS.length) {
    chips.push({
      key: 'areas',
      label:
        s.areas.length === 0 ? 'No areas' : `In ${s.areas.map((a) => AREA_NAMES[a]).join(' + ')}`,
      clear: () => onChange({ ...s, areas: [...ALL_AREAS] }),
    });
  }
  if (s.largerThan) {
    chips.push({
      key: 'largerThan',
      label: `Larger than ${sizeLabel(s.largerThan)}`,
      clear: () => onChange({ ...s, largerThan: '' }),
    });
  }
  if (s.smallerThan) {
    chips.push({
      key: 'smallerThan',
      label: `Smaller than ${sizeLabel(s.smallerThan)}`,
      clear: () => onChange({ ...s, smallerThan: '' }),
    });
  }
  if (s.modifiedNewerThan) {
    chips.push({
      key: 'modifiedNewerThan',
      label: `Modified in the last ${ageLabel(s.modifiedNewerThan)}`,
      clear: () => onChange({ ...s, modifiedNewerThan: '' }),
    });
  }
  if (s.modifiedOlderThan) {
    chips.push({
      key: 'modifiedOlderThan',
      label: `Not modified for ${ageLabel(s.modifiedOlderThan)}`,
      clear: () => onChange({ ...s, modifiedOlderThan: '' }),
    });
  }
  if (s.addedNewerThan) {
    chips.push({
      key: 'addedNewerThan',
      label: `Added in the last ${ageLabel(s.addedNewerThan)}`,
      clear: () => onChange({ ...s, addedNewerThan: '' }),
    });
  }
  if (s.addedOlderThan) {
    chips.push({
      key: 'addedOlderThan',
      label: `Added over ${ageLabel(s.addedOlderThan)} ago`,
      clear: () => onChange({ ...s, addedOlderThan: '' }),
    });
  }
  return chips;
}

/** "12 files · 3.4 MB" for a header or a card. */
export function describeTotals(count: number, bytes: number): string {
  return `${count.toLocaleString()} ${count === 1 ? 'file' : 'files'} · ${formatBytes(bytes)}`;
}
